import { FormEvent, useEffect, useState } from 'react';
import './LoginPage.css';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import UsersService from '../../common/Rest/Services/UserService/UsersService';
import { LoginData } from '../../common/Rest/Services/UserService/Data';
import { useAuth } from '../../common/Auth/AuthProvider';
import { NavLink, useNavigate } from 'react-router-dom';
import Routes from '../../common/Routing/Routes';
import TelegramLoginButton from './TelegramLoginButton';
import { AppConfig } from '../../../Config';
import GoogleIdentityButton from './GoogleIdentityButton';
import GoogleAccountLinkPanel from './GoogleAccountLinkPanel';

interface LoginPageProps {
  onLoginSuccess?: () => void;
  onClose?: () => void;
  redirectTo?: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess, onClose, redirectTo }) => {
  const [loginData, setLoginData] = useState<LoginData>({ email: '', password: '' });
  const [loginError, setLoginError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingGoogleLink, setPendingGoogleLink] = useState<{
    idToken: string;
    maskedEmail?: string | null;
  } | null>(null);
  const { isAuthenticated, isAuthInitialized, login } = useAuth();
  const navigate = useNavigate();
  const isTelegramLoginAvailable = Boolean(Number(AppConfig.TelegramLoginClientId));
  const isGoogleLoginAvailable = Boolean(AppConfig.GoogleLoginClientId);

  const completeLogin = (token: string) => {
    login(token);
  };

  useEffect(() => {
    if (!isAuthInitialized || !isAuthenticated) {
      return;
    }

    onLoginSuccess?.();
    if (!onLoginSuccess) {
      navigate(redirectTo || Routes.PROFILE, { replace: true });
    }
  }, [isAuthenticated, isAuthInitialized, navigate, onLoginSuccess, redirectTo]);

  if (!isAuthInitialized || isAuthenticated) {
    return null;
  }

  const handleLogin = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!loginData.email.trim() || !loginData.password) {
      setLoginError('Введіть email і пароль.');
      return;
    }

    try {
      setSubmitting(true);
      setLoginError(null);
      setPendingGoogleLink(null);
      const response = await UsersService.login({
        email: loginData.email.trim(),
        password: loginData.password,
      });
      completeLogin(response.token);
    } catch (error) {
      setLoginError('Не вдалося увійти. Перевірте email і пароль.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    try {
      setSubmitting(true);
      setLoginError(null);
      setPendingGoogleLink(null);
      const response = await UsersService.loginWithGoogle({ idToken });
      if (response.requiresAccountLink) {
        setPendingGoogleLink({
          idToken,
          maskedEmail: response.maskedEmail,
        });
        return;
      }

      if (response.auth?.token) {
        completeLogin(response.auth.token);
        return;
      }

      setLoginError('Google підтвердив вхід, але Readerly не отримав токен.');
    } catch {
      setLoginError('Не вдалося увійти через Google. Спробуйте ще раз.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="login-page" onSubmit={handleLogin} noValidate>
      <TextField
        id="login-email"
        label="Email"
        type="email"
        variant="outlined"
        value={loginData.email}
        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
        fullWidth
      />

      <TextField
        id="login-password"
        label="Пароль"
        type={showPassword ? 'text' : 'password'}
        variant="outlined"
        value={loginData.password}
        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Приховати пароль' : 'Показати пароль'}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  tabIndex={-1}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        fullWidth
      />

      <div className="forgot-password-link">
        <NavLink onClick={onClose} to={Routes.PASSWORD_RECOVERY}>
          Забули пароль?
        </NavLink>
      </div>

      {loginError && <div className="error-message">{loginError}</div>}

      {pendingGoogleLink && (
        <GoogleAccountLinkPanel
          idToken={pendingGoogleLink.idToken}
          maskedEmail={pendingGoogleLink.maskedEmail}
          onAuthenticated={completeLogin}
          onCancel={() => setPendingGoogleLink(null)}
        />
      )}

      <Button id="login-button" variant="contained" type="submit" disabled={submitting} fullWidth>
        {submitting ? 'Входимо...' : 'Увійти'}
      </Button>

      {(isGoogleLoginAvailable || isTelegramLoginAvailable) && (
        <>
          <div className="auth-divider">
            <span>або</span>
          </div>

          {isGoogleLoginAvailable && (
            <GoogleIdentityButton
              disabled={submitting}
              text="signin_with"
              onCredential={handleGoogleCredential}
              onError={(message) => setLoginError(message || null)}
            />
          )}

          <TelegramLoginButton
            disabled={submitting}
            onAuthenticated={completeLogin}
            onError={(message) => setLoginError(message || null)}
          />
        </>
      )}

      <div className="auth-switch">
        Ще немає акаунта?{' '}
        <NavLink onClick={onClose} to={Routes.REGISTER}>
          Зареєструватися
        </NavLink>
      </div>
    </form>
  );
};

export default LoginPage;
